import React, { useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { AssetContainer, useAsset } from '~/techstyle-shared/react-assets';
import {
  Video,
  useCarouselContext,
  useListViewed as usePromoListViewed,
} from '~/techstyle-shared/react-components';

import { parseVideoIdInput } from '../../utils/assetHelper';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const VideoWithNoPointerEvents = styled(Video)`
  pointer-events: none;
`;

export default function TrackedAssetContainer({
  name = '',
  asset,
  url = '',
  children,
  ...props
}) {
  const trackedAssetRef = useRef(null);
  const assetData = useAsset(name);
  const carouselContext = useCarouselContext();
  const hasBeenObservedInCarouselRef = useRef(false);
  if (!asset && assetData) {
    const { assets = [] } = assetData.data;
    asset = assets[0];
  }

  const { videoId, videoTargetUrl } = parseVideoIdInput({ asset });
  const isVideoAsset = videoId;

  const promoListViewedHelpers = usePromoListViewed();
  const hasHelpers = Boolean(promoListViewedHelpers);
  const isVideoContainer = Boolean(url) || isVideoAsset;

  useEffect(() => {
    if (!hasHelpers) {
      return () => {};
    }

    const node = trackedAssetRef.current;
    const { observe, unobserve } = promoListViewedHelpers;

    // When we aren't in a carousel, we can observe the asset immediately
    if (!carouselContext && node) {
      observe(node, { ...asset, isVideoAsset });
    }

    return () => unobserve(node);
  }, [
    hasHelpers,
    promoListViewedHelpers,
    asset,
    isVideoAsset,
    carouselContext,
  ]);

  // Handle tracking for carousel contexts differently, otherwise it can cause
  // all slides to be tracked at once before flickity initializes.
  useEffect(() => {
    if (!hasHelpers || !carouselContext?.flickity) {
      return () => {};
    }

    const node = trackedAssetRef.current;
    const { observe, unobserve } = promoListViewedHelpers;
    const flickity = carouselContext.flickity;
    const slideNumber = carouselContext.slideNumber;

    // Check initial state and set up tracking
    const isInitiallySelected = slideNumber === flickity.selectedIndex;
    if (node && isInitiallySelected && !hasBeenObservedInCarouselRef.current) {
      observe(node, { ...asset, isVideoAsset });
      hasBeenObservedInCarouselRef.current = true;
    }

    // Listen for slide changes, we only want to observe it after it
    // becomes selected the first time.
    const handleSlideChange = (index) => {
      if (
        node &&
        index === slideNumber &&
        !hasBeenObservedInCarouselRef.current
      ) {
        observe(node, { ...asset, isVideoAsset });
        hasBeenObservedInCarouselRef.current = true;
      }
    };

    flickity.on('change', handleSlideChange);

    return () => {
      flickity.off('change', handleSlideChange);
      if (node) {
        unobserve(node);
      }
    };
  }, [
    hasHelpers,
    promoListViewedHelpers,
    asset,
    isVideoAsset,
    carouselContext,
  ]);

  if (children) {
    return <Wrapper ref={trackedAssetRef}>{children}</Wrapper>;
  }

  if (isVideoContainer) {
    return (
      <Wrapper ref={trackedAssetRef}>
        {videoTargetUrl ? (
          <a href={videoTargetUrl}>
            <VideoWithNoPointerEvents url={url} {...props} />
          </a>
        ) : (
          <Video url={url} {...props} />
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper ref={trackedAssetRef}>
      <AssetContainer name={name} {...props} />
    </Wrapper>
  );
}

TrackedAssetContainer.propTypes = {
  asset: PropTypes.object,
  children: PropTypes.node,
  isVideoAsset: PropTypes.bool,
  name: PropTypes.string,
  url: PropTypes.string,
};
